import React, { ReactElement, useEffect, useState } from 'react';
import { PreviewModes, PurcsPreview } from '@vp/react-product-preview';
import {
  FlexBox,
  FluidImage,
  ModalDialog,
  ModalDialogBody,
  ModalDialogCloseButton,
  ModalDialogContent,
  ModalDialogNav,
  Typography,
} from '@vp/swan';
import { useProductContext } from 'src/hooks/ProductContextHooks';
import { useTrackingContext } from 'src/hooks/TrackingContextHooks';
import { useUploadContext } from 'src/hooks/UploadContextHooks';
import previewPlaceholder from 'src/images/preview-placeholder.svg';
import { PurcsPreviewImage } from 'src/services/UploadsService';
import { REQUESTOR } from 'src/utils/Constants';
import { currentCulture } from 'src/utils/cultureUtil';
import { text } from 'src/utils/localizationUtil';
import { createTrackingDataForProduct } from 'src/utils/trackingUtil';
import { AnalyticsEventData, sendEventTracking } from '../Analytics';

export interface PreviewerProps {
  maxHeight: number;
}

const Previewer = ({ maxHeight }: PreviewerProps): ReactElement => {
  const { upload } = useUploadContext();
  const { completedProductInformation } = useProductContext();
  const [selectedPreviewUrl, setSelectedPreviewUrl] = useState<string | null>(null);
  const [isFullSizeModalOpen, setIsFullSizeModalOpen] = React.useState(false);
  const canShowPreview = upload?.renderingInstructionsUrl && completedProductInformation; //Defined so that the useEffect does not need a dependency on upload (which changes often)
  const { trackingConfiguration } = useTrackingContext();
  const { productInformation } = useProductContext();

  useEffect(() => {
    if (canShowPreview) {
      if (trackingConfiguration && productInformation.mpvId) {
        sendEventTracking({
          ...trackingConfiguration,
          ...createTrackingDataForProduct({
            mpvId: productInformation.mpvId,
            productKey: productInformation.productKey,
            productName: productInformation.productName,
            productVersion: productInformation.productVersion,
          }),
          action: 'Success Message Displayed',
          category: 'Design - Upload and Print',
          label: 'Upload Success',
        } as AnalyticsEventData);
      }
    }
  }, [
    canShowPreview,
    trackingConfiguration,
    productInformation.mpvId,
    productInformation.productKey,
    productInformation.productName,
    productInformation.productVersion,
  ]);

  const fullSizeButtonOnClick = (preview: PurcsPreviewImage): void => {
    if (preview?.src) {
      setSelectedPreviewUrl(preview.src);
      setIsFullSizeModalOpen(true);
    }
  };

  const maxImageWidth = 1000;
  const purcsImageWidth = 2000;

  return (
    <>
      {upload?.renderingInstructionsUrl && completedProductInformation ? (
        <>
          <div
            className="swan-mt-8"
            style={{
              maxWidth: maxImageWidth,
              margin: '0 auto',
            }}
          >
            <PurcsPreview
              accessibleTextForNext="Next"
              accessibleTextForPrevious="Previous"
              requestor={REQUESTOR}
              locale={currentCulture}
              width={purcsImageWidth}
              previewMode={PreviewModes.Carousel}
              previewInstructionsUri={upload.renderingInstructionsUrl}
              optionSelections={completedProductInformation.selectedOptions}
              coreProductKey={completedProductInformation.productKey}
              productVersion={completedProductInformation.productVersion}
              pageSelectorOptions={{ show: true, pageOptionText: text('purcs-page-dropdown-option') }}
              fullSizeButtonOptions={{
                show: true,
                text: text('view-full-size'),
                onClick: fullSizeButtonOnClick,
              }}
              maxHeight={maxHeight}
            />
          </div>
          <ModalDialog isOpen={isFullSizeModalOpen} onRequestDismiss={() => setIsFullSizeModalOpen(false)} takeOver>
            <ModalDialogContent>
              <ModalDialogNav>
                <ModalDialogCloseButton visuallyHiddenLabel={text('close')} />
              </ModalDialogNav>
              <ModalDialogBody style={{ textAlign: 'center' }}>
                <FluidImage
                  src={selectedPreviewUrl ? selectedPreviewUrl : undefined}
                  alt=""
                  style={{ maxWidth: purcsImageWidth }}
                />
              </ModalDialogBody>
            </ModalDialogContent>
          </ModalDialog>
        </>
      ) : (
        <FlexBox
          justifyContent="center"
          alignItems="center"
          style={{
            width: '100%',
            height: '100%',
          }}
        >
          <FlexBox justifyContent="center" flexDirection="column">
            <img src={previewPlaceholder} alt="" />
            <Typography marginTop={'5'} fontSize={'small'} textAlign="center">
              {text('preview-label')}
            </Typography>
          </FlexBox>
        </FlexBox>
      )}
    </>
  );
};

export default Previewer;
